import React, { useState, useMemo } from "react";
import HoldingsSection from "./holding_section";
import GoldCard from "./metal_card";
import DiamondPointsCard from "./diamond_points_section";
import { toast } from "react-toastify";

import { useDispatch, useSelector } from "react-redux";
import {
  fetchThemeRequest,
  createThemeRequest,
  clearThemeMessages,
} from "../../../redux/AppTheme/AppThemeSlice";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { canShowPermission } from "../Permissions/permissionObject";

const colorConfigs = [
  {
    key: "primaryColor",
    label: "Primary Color",
    default: "#250164",
    description: "Main color for app elements and highlights.",
  },
  {
    key: "scaffoldBackgroundColor",
    label: "Scaffold Background",
    default: "#FFFFFF",
    description: "App page (scaffold) background color.",
  },
  {
    key: "containerColor",
    label: "Container Color",
    default: "#F5F5F5",
    description: "Color for containers and cards.",
  },
  {
    key: "textColor",
    label: "Text Color in APP",
    default: "#FFFFFF",
    description: "Color for text in the app.",
  },
];

export default function SimplifiedThemeEditor() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const themeState = useSelector((state) => state.theme);
  useEffect(() => {
    dispatch(fetchThemeRequest());
  }, [dispatch]);

  const goToThemeHistory = () => {
    navigate("/AppThemeHistory");
  };

  useEffect(() => {
    const list = themeState?.theme;
    if (Array.isArray(list) && list.length > 0) {
      const t0 = list[0];
      setTheme({
        primaryColor: t0.primaryColor || "#250164",
        scaffoldBackgroundColor:
          t0.scaffoldBackgroundColor || t0.scaffoldColor || "#131313",
        containerColor: t0.containerColor || "#131313",
        gradientColors:
          Array.isArray(t0.gradientColors) && t0.gradientColors.length
            ? t0.gradientColors
            : ["#131313", "#250164", "#250164", "#131313"],
        textColor: t0.textColor || "#FFFFFF",
      });
    }
  }, [themeState.theme]);

  const [theme, setTheme] = useState({
    primaryColor: "#250164",
    scaffoldBackgroundColor: "#131313",
    containerColor: "#040404ff",
    gradientColors: ["#131313", "#250164", "#250164", "#131313"],
    textColor: "#FFFFFF",
  });

  const [brightness, setBrightness] = useState("light");

  const handleColorChange = (key, value) => {
    // if (theme[key] !== value) {
    //   toast.info(`${key} changed to ${value}`); //when we change color should show toast msg
    // }
    setTheme({ ...theme, [key]: value });
  };

  const handleGradientColorChange = (index, value) => {
    // if (theme.gradientColors[index] !== value) {
    //   toast.info(`Gradient Color ${index + 1} changed to ${value}`);//when we gradientColors color should show toast msg
    // }
    const updatedColors = [...theme.gradientColors];
    updatedColors[index] = value;
    setTheme({ ...theme, gradientColors: updatedColors });
  };

  const addGradientColor = () => {
    setTheme({
      ...theme,
      gradientColors: [...theme.gradientColors, "#ffffff"], //add gradient colors in array
    });
  };
  const removeGradientColor = (index) => {
    const updatedColors = theme.gradientColors.filter((_, i) => i !== index);
    setTheme({ ...theme, gradientColors: updatedColors });
  };

  function buildThemePayload() {
    return {
      primaryColor: theme.primaryColor,
      scaffoldColor: theme.scaffoldBackgroundColor,
      containerColor: theme.containerColor,
      gradientColors: theme.gradientColors,
      textColor: theme.textColor,
      brightness,
    };
  }
  function saveTheme() {
    const confirmSave = window.confirm(
      "Are you sure you want to apply the new theme changes?"
    );
    if (!confirmSave) return;

    const payload = buildThemePayload();

    setLoading(true); // start loader
    dispatch(createThemeRequest(payload));
    toast.success("App Theme Saved Successfully");
    // fallback in case Redux never responds
    setTimeout(() => {
      setLoading(false);
    }, 5000);
  }

  useEffect(() => {
    if (themeState.message) {
      toast.success(themeState.message);
      setLoading(false); // stop loader
      dispatch(clearThemeMessages());
    }
    if (themeState.error) {
      toast.error(themeState.error);
      setLoading(false); // stop loader
      dispatch(clearThemeMessages());
    }
  }, [themeState.message, themeState.error, dispatch]);

  const [permissions, setPermissions] = useState({});
  useEffect(() => {
    const storedPermissions = localStorage.getItem("userPermissions");
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (error) {
        console.error("Error parsing stored permissions", error);
      }
    }
  }, []);
  const canCreateTheme = canShowPermission(permissions, "appThemeSelector", "create");
  const canDeleteTheme = canShowPermission(permissions, "appThemeSelector", "delete");
  const canEditTheme = canShowPermission(permissions, "appThemeSelector", "edit");

  const gradientPreview = useMemo(() => {
    return `linear-gradient(180deg, ${theme.gradientColors.join(", ")})`;
  }, [theme.gradientColors]);
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        height: "100vh",
        fontFamily: "Arial, sans-serif",
        paddingTop: "70px",
      }}
    >
      {/* Color selection panel */}
      <div
        style={{
          flex: 2.5,
          overflowY: "auto",
          padding: 16,
          boxSizing: "border-box",

          borderRight: "1px solid #ddd",
          backgroundColor: "#f9f9f9",
        }}
      >
        <h2
          style={{
            textAlign: "center",
            padding: "16px 0",
            margin: "0 0 24px 0",
            backgroundColor: theme.primaryColor,
            color: "#fff",
            borderRadius: "8px",
          }}
        >
          Customize Theme Colors
        </h2>

        {/* Linear Gradient Section */}
        <div
          style={{
            background: "#fff",
            borderRadius: 12,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            padding: "20px",
            marginBottom: "20px",
          }}
        >
          <h3
            style={{
              color: theme.primaryColor,
              marginTop: 0,
              marginBottom: 16,
              fontSize: 18,
              fontWeight: 600,
            }}
          >
            Linear Gradient
          </h3>

          {/* Gradient Preview */}
          <div
            style={{
              width: "100%",
              height: "80px",
              background: gradientPreview,
              borderRadius: 8,
              marginBottom: 16,
              border: "2px solid #e0e0e0",
            }}
          />

          <div style={{ fontSize: 14, color: "#666", marginBottom: 16 }}>
            Create beautiful gradients for your app background and elements.
          </div>

          {/* Gradient Color Controls */}
          <div style={{ marginBottom: 16 }}>
            {theme.gradientColors.map((color, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: 12,
                  gap: 10,
                }}
              >
                <input
                  type="color"
                  value={color}
                  onChange={(e) =>
                    handleGradientColorChange(index, e.target.value)
                  }
                  style={{
                    width: "50px",
                    height: "35px",
                    border: "none",
                    borderRadius: 6,
                    cursor: "pointer",
                  }}
                />
                <span
                  style={{
                    fontSize: 14,
                    color: "#333",
                    minWidth: "70px",
                    fontFamily: "monospace",
                  }}
                >
                  {color}
                </span>
                <span style={{ fontSize: 14, color: "#888" }}>
                  Color {index + 1}
                </span>
                {canDeleteTheme && theme.gradientColors.length > 2 && (
                  <button
                    onClick={() => removeGradientColor(index)}
                    style={{
                      background: "#ff4444",
                      color: "white",
                      border: "none",
                      borderRadius: 4,
                      padding: "4px 8px",
                      fontSize: 12,
                      cursor: "pointer",
                      marginLeft: "auto",
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>

          {(canCreateTheme || canEditTheme) && (
            <button
              onClick={addGradientColor}
              style={{
                background: theme.primaryColor,
                color: "white",
                border: "none",
                borderRadius: 6,
                padding: "8px 16px",
                fontSize: 14,
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              + Add Color
            </button>
          )}
        </div>

        {/* Individual Colors Section */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: 16,
            marginBottom: 20,
          }}
        >
          {colorConfigs.map(({ key, label, description }) => (
            <div
              key={key}
              style={{
                background: "#fff",
                borderRadius: 12,
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                padding: "20px",
                display: "flex",
                alignItems: "center",
                gap: 16,
              }}
            >
              <input
                type="color"
                value={theme[key]}
                onChange={(e) => handleColorChange(key, e.target.value)}
                style={{
                  width: "60px",
                  height: "60px",
                  border: "none",
                  borderRadius: 8,
                  cursor: "pointer",
                }}
              />
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 16,
                    marginBottom: 4,
                    color: theme.primaryColor,
                  }}
                >
                  {label}
                </div>
                <div
                  style={{
                    fontSize: 14,
                    color: "#333",
                    marginBottom: 4,
                    fontFamily: "monospace",
                  }}
                >
                  {theme[key]}
                </div>
                <div style={{ fontSize: 13, color: "#666", lineHeight: 1.3 }}>
                  {description}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Controls Section */}
        <div
          style={{
            padding: "20px",
            textAlign: "center",
            background: "#fff",
            borderRadius: 12,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            position: "sticky",
            bottom: 16,
          }}
        >
          <button
            onClick={goToThemeHistory}
            style={{
              padding: "12px 32px",
              fontSize: 16,
              fontWeight: "600",
              color: "#fff",
              background: "#11176dff",
              border: "none",
              borderRadius: 8,
              cursor: "pointer",
              marginBottom: "10px",
              marginRight: "12px",
            }}
          >
            View Theme History
          </button>
          {canCreateTheme && (
            <button
              onClick={saveTheme}
              disabled={loading}
              style={{
                padding: "12px 32px",
                fontSize: 16,
                fontWeight: "600",
                color: "#fff",
                background: gradientPreview,
                border: "none",
                borderRadius: 8,
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.7 : 1,
                boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                transition: "transform 0.2s ease",
              }}
            >
              {loading ? "Saving..." : "Save Theme"}
            </button>
          )}
        </div>
      </div>

      {/* Preview panel */}
      <div
        style={{
          flex: 1.2,
          backgroundColor: "#f3f3f3",
          padding: 16,
          overflowY: "auto",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            width: "360px",
            height: "700px",
            background: gradientPreview,
            borderRadius: 25,
            border: "8px solid #333",
            position: "relative",
            overflow: "hidden",
            boxShadow: "0 15px 40px rgba(0,0,0,0.4)",
            marginTop: "40px",
          }}
        >
          {/* Status Bar */}
          <div
            style={{
              height: "30px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0 20px",
              color: "white",
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            <span>1:09</span>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <span>●●●●</span>
              <span>📶</span>
              <span>🔋</span>
            </div>
          </div>
          {/* Profile Section */}
          <div
            style={{
              padding: "10px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div
              style={{
                width: "30px",
                height: "30px",
                borderRadius: "50%",
                background: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: theme.primaryColor,
                fontWeight: "bold",
                fontSize: "10px",
              }}
            >
              V
            </div>
            <div
              style={{
                width: "30px",
                height: "30px",
                borderRadius: "50%",
                border: `2px solid ${theme.scaffoldBackgroundColor}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                background: "#fff",
                fontSize: "10px",
              }}
            >
              🔔
            </div>
          </div>
          <HoldingsSection
            containerColor={theme.containerColor}
            valueAmount="504.00"
            cashAmount="0.00"
            textColor={theme.textColor}
          />
          <GoldCard
            textcolor={theme.textColor}
            metalName="Gold"
            purity="24K-999"
            pricePerGram="9000.0"
            totalValue="504.00"
            weight="0.056 g"
            metalColor="#FFD700"
          />
          {/* Trading Section */}
          <div
            style={{
              background: theme.containerColor,
              margin: "0 10px",
              borderRadius: "20px",
              padding: "10px",
              opacity: 0.9,
            }}
          >
            {/* Gold Selection */}
            <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
              <button
                style={{
                  flex: 1,
                  padding: "10px",
                  background: theme.primaryColor,
                  border: "none",
                  borderRadius: "25px",
                  flexDirection: "column",
                  color: theme.textColor,
                  fontWeight: "600",
                }}
              >
                Gold 24K-999
              </button>
            </div>

            {/* Input Fields */}
            <div style={{ display: "flex", gap: "10px", marginBottom: "15px" }}>
              <input
                style={{
                  flex: 0.5,
                  padding: "8px",
                  background: "transparent",
                  border: "1px solid rgba(255,255,255,0.3)",
                  borderRadius: "10px",
                  color: "white",
                  fontSize: "10px",
                }}
                placeholder="Enter Grams"
              />
              <div
                style={{
                  width: "30px",
                  height: "30px",
                  background: "#fff",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  alignSelf: "center",
                }}
              >
                ⇄
              </div>
              <input
                style={{
                  flex: 0.5,
                  padding: "8px",
                  background: "transparent",
                  border: "1px solid rgba(255,255,255,0.3)",
                  borderRadius: "10px",
                  color: "white",
                  fontSize: "10px",
                }}
                placeholder="Enter Amount"
              />
            </div>

            {/* Amount Buttons */}
            <div style={{ display: "flex", gap: "8px", marginBottom: "20px" }}>
              {["₹ 101", "₹ 501", "₹ 1001", "₹ 5001"].map((amount) => (
                <button
                  key={amount}
                  style={{
                    flex: 1,
                    padding: "8px",
                    background: "transparent",
                    border: "1px solid rgba(255,255,255,0.4)",
                    borderRadius: "20px",
                    color: theme.textColor,
                    fontSize: "10px",
                  }}
                >
                  {amount}
                </button>
              ))}
            </div>

            {/* Price Info */}
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
              <div
                style={{
                  color: theme.textColor,
                  fontSize: "12px",
                  marginBottom: "8px",
                }}
              >
                Current Price (exclusive of 3% gst):
              </div>
              <div
                style={{
                  color: theme.textColor,
                  fontSize: "12px",
                  fontWeight: "bold",
                }}
              >
                ₹ 9000.0/gm
              </div>
            </div>

            {/* Buy Button */}
            <button
              style={{
                width: "100%",
                padding: "10px",
                background: theme.primaryColor,
                border: "none",
                borderRadius: "25px",
                color: theme.textColor,
                fontSize: "14px",
                fontWeight: "600",
              }}
            >
              Buy
            </button>
          </div>
          <div>
            <DiamondPointsCard textcolor={theme.textColor} />
          </div>
          {/* Bottom Navigation */}
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "70px",
              borderRadius: "20px",
              border: "1px solid rgba(255, 255, 255, 0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-around",
              opacity: 0.95,
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  color: "white",
                  fontSize: "20px",
                  marginBottom: "4px",
                }}
              >
                🏠
              </div>
              <div
                style={{
                  color: theme.textColor,
                  fontSize: "12px",
                  fontWeight: "600",
                }}
              >
                Home
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  color: theme.textColor,
                  fontSize: "20px",
                  marginBottom: "4px",
                }}
              >
                💼
              </div>
              <div style={{ color: theme.textColor, fontSize: "12px" }}>
                Savings
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  color: theme.scaffoldBackgroundColor,
                  fontSize: "20px",
                  marginBottom: "4px",
                }}
              >
                🛒
              </div>
              <div
                style={{
                  color: theme.textColor,
                  fontSize: "12px",
                  fontWeight: "600",
                }}
              >
                Buy
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  color: theme.textColor,
                  fontSize: "20px",
                  marginBottom: "4px",
                }}
              >
                🏪
              </div>
              <div style={{ color: theme.textColor, fontSize: "12px" }}>
                Store
              </div>
            </div>
            <div style={{ textAlign: "center", position: "relative" }}>
              <div
                style={{
                  color: theme.textColor,
                  fontSize: "20px",
                  marginBottom: "4px",
                }}
              >
                🧭
              </div>
              <div style={{ color: theme.textColor, fontSize: "12px" }}>
                Explore
              </div>
              <div
                style={{
                  position: "absolute",
                  top: "0",
                  right: "8px",
                  width: "8px",
                  height: "8px",
                  background: "#ff4444",
                  borderRadius: "50%",
                }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
